import { createAmbulatoryLearningPathInput } from "./ambulatory/input";
import { createLoader } from "./loader";
import { createShadowRoot, html } from "../lib";
import { createWarning } from "./warning";
import { getSession } from "../session";
import { getUserByEmail, getDbUserById, getDbMemberById } from "../api";
import { createAcuteLearningPathInput } from "./acute/input";

const template = document.createElement("template");

const contentId = "content";
const acuteId = "acute";
const ambulatoryId = "ambulatory";

template.innerHTML = html`
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    legend {
      color: #595959;
      font-weight: bold;
    }

    fieldset {
      border-color: rgba(0, 0, 0, 0.4);
      border-radius: 4px;
      border-style: solid;
      border-width: 1px;
      display: flex;
      gap: 20px;
      padding: 20px 20px;
      flex-direction: column;
    }

    p {
      color: rgba(0, 0, 0, 0.6);
    }
  </style>

  <legend>Learning Paths</legend>
  <fieldset>
    <p>
      OCHIN Learning Paths are specialized training programs developed by OCHIN
      to prepare your staff for the transition to OCHIN Epic and our Preferred
      Technology Partner (PTP) integrations.
    </p>
    <p>
      If you have any questions about how best to leverage this resource, please
      reach out to the Project Manager assigned to assist with your install
      process.
    </p>

    <div id="${contentId}"></div>

    <div id="${ambulatoryId}"></div>
    <div id="${acuteId}"></div>
  </fieldset>
`;

class El extends HTMLElement {
  #acute;
  #ambulatory;
  #content;

  constructor() {
    super();

    const { getElementById } = createShadowRoot(this, template);

    this.#acute = getElementById(acuteId);
    this.#ambulatory = getElementById(ambulatoryId);
    this.#content = getElementById(contentId);

    const el = this;
    (async () => {
      await create(el);
    })();
  }

  set acute(content) {
    this.#acute.replaceChildren(content);
  }

  set ambulatory(content) {
    this.#ambulatory.replaceChildren(content);
  }

  set content(content) {
    this.#content.replaceChildren(content);
  }
}

customElements.define("o-learning-path", El);

const create = async el => {
  const session = getSession();
  const { serviceAreaId, organizationId } = session.user;
  const saDisplayName = `ochin-crowd-sa${serviceAreaId}`;

  const { pathname } = location;
  const match = pathname.match(new RegExp("/build/user/edit/(\\d+)"));
  if (match === null) {
    el.content = createWarning("unable to match url");
    return 0;
  }

  const userIdStr = match[1];
  if (typeof userIdStr === "undefined") {
    el.content = createWarning("unable to match userId from url");
    return 0;
  }

  let userId;
  try {
    userId = parseInt(userIdStr, 10);
  } catch {
    el.content = createWarning("unable to parse userId from url");
    return 0;
  }

  el.content = createLoader(
    "Loading member organization and user from Install Tool database"
  );
  const [dbMember, dbUser] = await Promise.all([
    getDbMemberById(organizationId),
    getDbUserById({ saDisplayName, userId }),
  ]);
  if (dbMember instanceof Error) {
    console.error(dbMember);
    el.content = createWarning("member organization not found");
    return 0;
  }
  if (dbUser instanceof Error) {
    console.error(dbMember);
    el.content = createWarning("user not found");
    return 0;
  }

  const { email } = dbUser;
  const { careSetting } = dbMember;

  el.content = createLoader("Loading user from Entra");
  const user = await getUserByEmail({ mail: email, saGroup: saDisplayName });
  if (user instanceof Error) {
    console.error(dbMember);
    el.content = createWarning("user does not exist in Entra");
    return 0;
  }

  const { userPrincipalName } = user;

  el.content = "";

  const ambulatory = createAmbulatoryLearningPathInput({
    saDisplayName,
    userPrincipalName,
  });
  el.ambulatory = ambulatory.el;
  await ambulatory.init();

  if (careSetting !== "Acute Care") return 0;
  const acute = createAcuteLearningPathInput({
    saDisplayName,
    userPrincipalName,
  });
  el.acute = acute.el;
  await acute.init();
};
